/**
 * Find partners component.
 *
 * Main component for searching and browsing potential language learning partners.
 * Provides filtering capabilities, partner detail views, and messaging functionality.
 * Handles responsive layout with mobile drawer for filters.
 *
 * @module FindPartners
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { logger } from "@/lib/logger";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Filter } from "lucide-react";
import { PartnerSearchFilters, Partner } from "@/types/partner-finder";
import PartnerDetailDrawer from "./PartnerDetailDrawer";
import { FilterPanel } from "./FilterPanel";
import { ActiveFilters } from "./ActiveFilters";
import { PartnerResults } from "./PartnerResults";
import { useNavigate } from "react-router-dom";
import { useIsLargeScreen } from "@/hooks/use-large-screen";
import { usePartnerFinderPath } from "@/hooks/usePartnerFinderPath";
import { useAuth } from "@/hooks/useAuth";
import { usePartnerFilters, useActiveFilters } from "@/hooks/usePartnerFilters";
import { useTranslation } from "react-i18next";

export default function FindPartners() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isLargeScreen = useIsLargeScreen();
  const paths = usePartnerFinderPath();
  const { user } = useAuth();
  const [filters, setFilters] = useState<PartnerSearchFilters>({});
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);

  // Use custom hook for partner filtering and fetching
  const { partners, isLoading } = usePartnerFilters(filters, user?.id);
  const activeFilters = useActiveFilters(filters);

  const handleRemoveFilter = (key: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (key === "availability") {
        delete next.availability;
      } else {
        delete next[key as keyof PartnerSearchFilters];
      }
      return next;
    });
  };

  const handleClearAll = () => {
    setFilters({});
  };

  const handleApplyFilters = (newFilters: PartnerSearchFilters) => {
    setFilters(newFilters);
    setIsFilterDrawerOpen(false);
  };

  const handlePartnerClick = (partner: Partner) => {
    setSelectedPartner(partner);
    setIsDetailDrawerOpen(true);
  };

  const handleMessage = (partner: Partner) => {
    if (!partner.userId) {
      logger.error(
        "Partner userId is missing, cannot send message",
        undefined,
        { partnerId: partner.id },
      );
      return;
    }
    navigate(paths.messages, {
      state: {
        partnerId: partner.userId,
        partnerName: partner.displayName,
        partnerLevel: partner.level,
      },
    });
  };

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Desktop: Filter Panel + Results */}
      <div className="hidden gap-6 lg:flex lg:gap-8">
        {/* Left: Filter Panel */}
        <aside className="w-64 lg:w-72 shrink-0">
          <Card>
            <CardContent className="p-4 lg:p-6">
              <h2 className="text-lg font-semibold mb-4">
                {t("partnerFinder.search.filtersTitle", {
                  defaultValue: "Filters",
                })}
              </h2>
              <FilterPanel
                filters={filters}
                isMobile={false}
                onApply={handleApplyFilters}
              />
            </CardContent>
          </Card>
        </aside>

        {/* Right: Results */}
        <div className="flex-1 min-w-0">
          {/* Active Filter Chips */}
          <ActiveFilters
            filters={activeFilters}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAll}
            className="mb-4"
          />

          {/* Results */}
          <PartnerResults
            partners={partners}
            isLoading={isLoading}
            onPartnerClick={handlePartnerClick}
            onMessage={handleMessage}
            onClearFilters={handleClearAll}
            isMobile={false}
          />
        </div>
      </div>

      {/* Mobile: Filter Drawer + Results */}
      <div className="space-y-4 lg:hidden">
        {/* Filter Button */}
        <Drawer open={isFilterDrawerOpen} onOpenChange={setIsFilterDrawerOpen}>
          <DrawerTrigger asChild>
            <Button variant="outline" className="w-full">
              <Filter className="h-4 w-4 mr-2" />
              {t("partnerFinder.search.filtersTitle", {
                defaultValue: "Filters",
              })}
              {activeFilters.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {activeFilters.length}
                </Badge>
              )}
            </Button>
          </DrawerTrigger>
          <DrawerContent className="max-h-[calc(100dvh-1rem)]">
            <DrawerHeader>
              <DrawerTitle>
                {t("partnerFinder.search.filtersTitle", {
                  defaultValue: "Filters",
                })}
              </DrawerTitle>
            </DrawerHeader>
            <div className="px-4 pb-4 overflow-y-auto">
              <FilterPanel
                filters={filters}
                isMobile={!isLargeScreen}
                onApply={handleApplyFilters}
              />
            </div>
          </DrawerContent>
        </Drawer>

        {/* Active Filter Chips */}
        <ActiveFilters
          filters={activeFilters}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAll}
        />

        {/* Results */}
        <PartnerResults
          partners={partners}
          isLoading={isLoading}
          onPartnerClick={handlePartnerClick}
          onMessage={handleMessage}
          onClearFilters={handleClearAll}
          isMobile={true}
        />
      </div>

      {/* Partner Detail Drawer */}
      {selectedPartner && (
        <PartnerDetailDrawer
          partner={selectedPartner}
          open={isDetailDrawerOpen}
          onOpenChange={setIsDetailDrawerOpen}
          onMessage={() => {
            handleMessage(selectedPartner);
            setIsDetailDrawerOpen(false);
          }}
        />
      )}
    </div>
  );
}
